import {
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useMemo, useRef, useState } from "react";
import { useReorderSplits, useSetAssignments } from "../api/splits.mutations";
import type { SplitsRealtime } from "../api/splits.realtime";
import {
  buildBoard,
  computeRoleSlots,
  conflictingCharacter,
  moveInOrder,
  parseDropTarget,
} from "../splits.logic";
import type {
  BoardCharacter,
  SplitItem,
  SplitsResponse,
} from "../splits.types";
import { CharacterCard } from "./CharacterCard";
import { CursorLayer } from "./CursorLayer";
import { SplitColumn, SplitColumnPreview } from "./SplitColumn";
import { UnassignedPool } from "./UnassignedPool";

// Empty sensor set disables dragging entirely (terminal-disconnected edit gating).
const NO_SENSORS: ReturnType<typeof useSensors> = [];

// Stable reference for empty columns so memoized SplitColumns aren't re-rendered by a fresh `[]`.
const EMPTY_CHARS: BoardCharacter[] = [];

// Owns the DndContext: turns drops into assignment mutations (optimistic, so the
// UI moves instantly) and previews same-player swaps with an amber ring. Also relays
// the local pointer + drag state to other officers and overlays their cursors/ghosts.
export function SplitsBoard({
  data,
  realtime,
  showNotes,
}: {
  data: SplitsResponse;
  realtime: SplitsRealtime;
  showNotes: boolean;
}) {
  // Remote cursor frames re-render this component ~20/s per peer, so memoize the board-derived
  // state (buildBoard sorts every bucket) on `data` — it never depends on cursor state — and keep
  // the child columns memoized (below) so cursor churn only re-renders the overlay.
  const { bySplit, unassigned, players, totalCharacters } = useMemo(
    () => buildBoard(data),
    [data],
  );
  const roleSlots = useMemo(() => computeRoleSlots(bySplit), [bySplit]);
  const setAssignments = useSetAssignments();
  const reorderSplits = useReorderSplits();
  const [activeCharacter, setActiveCharacter] = useState<BoardCharacter | null>(
    null,
  );
  const [activeSplit, setActiveSplit] = useState<SplitItem | null>(null);
  const [overSplitId, setOverSplitId] = useState<string | null>(null);
  const activeSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // The board container is the coordinate frame for cursors: fractions are measured against its
  // content box and remote cursors are absolutely positioned within it.
  const boardRef = useRef<HTMLDivElement>(null);
  // Last board-relative pointer fraction, so a drop can emit a ghost-clearing frame at the cursor.
  const lastFractionRef = useRef({ x: 0, y: 0 });
  // The character this client is dragging, folded into outgoing cursor frames (ref so the
  // pointermove closure reads the current value without re-binding the handler).
  const draggedCharacterIdRef = useRef<string | null>(null);
  // True during any drag. dnd-kit's DragOverlay (portaled over the board) fires a spurious
  // pointerleave at grab and swallows native pointermove, so we ignore the leave and drive
  // cursor frames from onDragMove for the duration instead.
  const isDraggingRef = useRef(false);

  const disconnected = realtime.status === "disconnected";

  // Character ids other officers are currently dragging → dim their source cards (same as local).
  // A sorted key gives a referentially-stable Set while a drag is in flight, so the memoized
  // columns don't re-render on every cursor frame — only when the dragged set actually changes.
  const draggingKey = [...realtime.cursors.values()]
    .map((c) => c.draggedCharacterId)
    .filter((id): id is string => id !== null)
    .sort()
    .join(",");
  const remoteDraggingIds = useMemo(
    () => new Set(draggingKey ? draggingKey.split(",") : []),
    [draggingKey],
  );

  // Flat character lookup for resolving remote drag ghosts by id.
  const charactersById = useMemo(() => {
    const map = new Map<string, BoardCharacter>();
    for (const list of bySplit.values()) for (const c of list) map.set(c.id, c);
    for (const c of unassigned) map.set(c.id, c);
    return map;
  }, [bySplit, unassigned]);

  // A character's current split (null = unassigned), read from the response.
  const originSplitId = (characterId: string) =>
    data.assignments.find((a) => a.characterId === characterId)?.splitId ??
    null;

  // The same-player card the hovered split already holds — highlighted as the swap target.
  const conflict =
    activeCharacter && overSplitId
      ? conflictingCharacter(bySplit.get(overSplitId) ?? [], activeCharacter)
      : undefined;

  // Emit a cursor frame from board-relative fractions of an absolute client point.
  function emitCursor(clientX: number, clientY: number) {
    const el = boardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    lastFractionRef.current = { x, y };
    realtime.sendCursor({
      x,
      y,
      draggedCharacterId: draggedCharacterIdRef.current,
    });
  }

  function handlePointerMove(e: React.PointerEvent) {
    emitCursor(e.clientX, e.clientY);
  }

  function handlePointerLeave() {
    // Ignore the DragOverlay-induced leave at grab; keep relaying for the whole drag.
    if (isDraggingRef.current) return;
    realtime.hideCursor();
  }

  // dnd-kit is the authoritative pointer source while dragging (native pointermove is swallowed
  // by the overlay): reconstruct the client point from the activator event + cumulative delta.
  function handleDragMove({ activatorEvent, delta }: DragMoveEvent) {
    const activator = activatorEvent as PointerEvent;
    emitCursor(activator.clientX + delta.x, activator.clientY + delta.y);
  }

  function handleDragStart({ active }: DragStartEvent) {
    isDraggingRef.current = true;
    const data = active.data.current;
    if (data?.type === "split") {
      setActiveSplit(data.split as SplitItem);
      return;
    }
    const character = (data?.character as BoardCharacter) ?? null;
    setActiveCharacter(character);
    // Tag subsequent cursor frames with the dragged character so others see a ghost.
    draggedCharacterIdRef.current = character?.id ?? null;
  }

  function handleDragOver({ active, over }: DragOverEvent) {
    if (active.data.current?.type === "split") return;
    const target = over ? parseDropTarget(String(over.id)) : null;
    setOverSplitId(target?.kind === "split" ? target.splitId : null);
  }

  function reset() {
    isDraggingRef.current = false;
    setActiveCharacter(null);
    setActiveSplit(null);
    setOverSplitId(null);
    // Clear the ghost for everyone immediately (don't wait for the next pointer frame).
    if (draggedCharacterIdRef.current !== null) {
      draggedCharacterIdRef.current = null;
      realtime.sendCursor({
        ...lastFractionRef.current,
        draggedCharacterId: null,
      });
    }
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    const activeData = active.data.current;
    reset();
    if (!over) return;

    // Column reorder: dropping one header over another.
    if (activeData?.type === "split") {
      const fromId = (activeData.split as SplitItem).id;
      const toId =
        over.data.current?.type === "split"
          ? (over.data.current.splitId as string)
          : null;
      if (!toId || toId === fromId) return;
      const ordered = moveInOrder(
        data.splits.map((s) => s.id),
        fromId,
        toId,
      );
      reorderSplits.mutate(ordered);
      return;
    }

    const character = activeData?.character as BoardCharacter | undefined;
    if (!character) return;

    const origin = originSplitId(character.id);
    const target = parseDropTarget(String(over.id));
    if (!target) return;

    if (target.kind === "pool") {
      if (origin !== null) {
        setAssignments.mutate([{ characterId: character.id, splitId: null }]);
      }
      return;
    }

    const targetId = target.splitId;
    if (targetId === origin) return;

    // Swap: move the displaced same-player card back to where the dragged one came from.
    // Displaced first so the target slot is vacated before the incoming char lands (matters
    // for the cache patch order; the DB applies both in one transaction).
    const displaced = conflictingCharacter(
      bySplit.get(targetId) ?? [],
      character,
    );
    const moves = displaced
      ? [
          { characterId: displaced.id, splitId: origin },
          { characterId: character.id, splitId: targetId },
        ]
      : [{ characterId: character.id, splitId: targetId }];
    setAssignments.mutate(moves);
  }

  return (
    <DndContext
      sensors={disconnected ? NO_SENSORS : activeSensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={reset}
    >
      <div
        ref={boardRef}
        className="relative max-w-7xl"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
      >
        <div className="flex select-none items-start gap-3 overflow-x-auto pr-[22rem]">
          <UnassignedPool
            unassigned={unassigned}
            totalCharacters={totalCharacters}
            players={players}
            remoteDraggingIds={remoteDraggingIds}
          />
          {data.splits.map((s) => (
            <SplitColumn
              key={s.id}
              split={s}
              chars={bySplit.get(s.id) ?? EMPTY_CHARS}
              roleSlots={roleSlots}
              showNotes={showNotes}
              highlightedCharacterId={
                overSplitId === s.id ? conflict?.id : undefined
              }
              remoteDraggingIds={remoteDraggingIds}
            />
          ))}
        </div>
        <CursorLayer
          cursors={realtime.cursors}
          members={realtime.members}
          charactersById={charactersById}
        />
      </div>
      {/* No drop animation — the optimistic move relocates the card/column instantly. */}
      <DragOverlay dropAnimation={null} className="cursor-default">
        {activeCharacter && <CharacterCard character={activeCharacter} />}
        {activeSplit && (
          <SplitColumnPreview
            split={activeSplit}
            chars={bySplit.get(activeSplit.id) ?? []}
            roleSlots={roleSlots}
            showNotes={showNotes}
          />
        )}
      </DragOverlay>
    </DndContext>
  );
}
